import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input, FormField } from '../../components/ui/Input';
import { authApi } from '../../api/auth';
import { useToast } from '../../contexts/ToastContext';
import { ApiError } from '../../api/client';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [refCode, setRefCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const stored = localStorage.getItem('altcoins.refCode');
    if (stored) setRefCode(stored);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await authApi.signup({ email, password, referralCode: refCode || undefined });
      toast.success('Account created. Check your inbox to verify your email.');
      setDone(true);
      setTimeout(() => nav('/auth/login'), 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md !p-8">
      <div className="text-center mb-8">
        <Link to="/" className="font-display font-bold text-2xl text-gradient">
          AltCoinsMoney
        </Link>
        <h1 className="font-display text-3xl font-bold mt-6">Create your account</h1>
        <p className="text-ink-600 text-sm mt-2">Takes 30 seconds</p>
      </div>

      {done ? (
        <div className="p-4 rounded-2xl bg-success/5 border border-success/20 text-success text-sm">
          Account created. Check your email to verify. Redirecting to login…
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField label="Email" htmlFor="email">
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </FormField>
          <FormField label="Password" htmlFor="password" hint="At least 8 characters">
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </FormField>
          <FormField label="Confirm password" htmlFor="confirm">
            <Input
              id="confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
          </FormField>
          <FormField
            label="Referral code"
            htmlFor="ref"
            hint={refCode ? 'Auto-applied from your referrer\'s link' : 'Optional'}
          >
            <Input
              id="ref"
              value={refCode}
              onChange={(e) => setRefCode(e.target.value.toUpperCase())}
              placeholder="ABCD1234"
              maxLength={12}
              className="font-mono"
            />
          </FormField>

          {error && (
            <div className="p-3 rounded-2xl bg-danger/5 border border-danger/20 text-danger text-sm">
              {error}
            </div>
          )}

          <Button type="submit" isLoading={loading} className="w-full">
            Create account
          </Button>
        </form>
      )}

      <div className="mt-6 text-center text-sm text-ink-600">
        Already have an account?{' '}
        <Link to="/auth/login" className="text-violet-600 hover:underline font-semibold">
          Sign in
        </Link>
      </div>
    </Card>
  );
}
